import { defineStore } from 'pinia';
import axios from '../../axios';

export const useWalletStore = defineStore('wallet', {
  state: () => ({
    balance: 0.00,
    currency: 'AUD',
    transactions: [],
    coupons: [],
    couponsFailed: false,   // true when the top-up offers couldn't be loaded (never hide this)
    loading: false,
    error: null,
  }),
  
  getters: {
    getBalance: (state) => state.balance,
    getCurrency: (state) => state.currency,
    getTransactions: (state) => state.transactions,
    isLoading: (state) => state.loading,
    getError: (state) => state.error,
  },
  
  actions: {
    /**
     * Fetch the wallet details from the backend.
     */
    async fetchWallet() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await axios.get('/transactions/get-balance/');
        // Endpoint returns a paginated list; a brand-new customer has no wallet row
        // yet, so guard against an empty result instead of crashing.
        const d = response.data;
        const row = Array.isArray(d?.results) ? d.results[0] : (Array.isArray(d) ? d[0] : d);
        this.balance = parseFloat(row?.balance ?? 0) || 0;
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to fetch wallet.';
        console.error('Error fetching wallet:', err);
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * Fetch the transaction history.
     */
    async fetchTransactions() {
      this.loading = true;
      this.error = null;
      
      try {
        const response = await axios.get('/transactions/wallet/');
        this.transactions = response.data
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to fetch transactions.';
        console.error('Error fetching transactions:', err);
      } finally {
        this.loading = false;
      }
    },
    
    async fetchCoupons(){
      this.loading = true;
      this.error = null;
      this.couponsFailed = false;

      try {
        const response = await axios.get('/transactions/coupons/wallet/');
        this.coupons = response.data.results;
      } catch (err) {
        // Must NOT fail silently: when this 403'd, the top-up offer tiles simply
        // vanished and customers paid without an offer, losing their bonus. Flag it
        // so the page can say the offers couldn't load instead of pretending
        // there are none.
        this.couponsFailed = true;
        this.error = err.response?.data?.message || 'Failed to fetch coupons.';
        console.error('Error fetching wallet coupons:', err);
      } finally {
        this.loading = false;
      }
    },
    /**
     * Recharge the wallet.
     * @param {Number} amount - The amount to recharge.
     */
    async rechargeWallet(amount) {
      this.loading = true;
      this.error = null;
      
      try {
        const payload = {
          amount: amount,
        };
        
        const response = await axios.post('/transactions/wallet/recharge/', payload);
        
        // Update the wallet balance and add the new transaction
        if (response.data.status === 'succeeded') {
          this.balance += amount;
          this.transactions.unshift({
            id: response.data.transaction_id,
            amount: amount,
            currency: this.currency,
            type: 'CREDIT',
            orderType: 'WALLET_RECHARGE',
            status: response.data.status,
            createdAt: new Date(),
            description: 'Wallet Recharge',
            receiptUrl: response.data.receipt_url,
          });
        }
        
        return response.data; // Return response for further handling if needed
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to recharge wallet.';
        console.error('Error recharging wallet:', err);
        throw err; // Re-throw for component-level handling
      } finally {
        this.loading = false;
      }
    },
    
    /**
     * Make a payment using the wallet.
     * @param {Number} amount - The amount to debit.
     * @param {String} orderId - The order ID for the transaction.
     */
    async makePayment(amount, orderId) {
      this.loading = true;
      this.error = null;
      
      try {
        const payload = {
          amount: amount,
          order_id: orderId, // Adjust based on your API requirements
        };
        
        // Uses the caxios wrapper, which already prepends the API base URL — so the
        // path must be relative ('/orders/create/'), not '/api/orders/create/' (that
        // double-prefixed to /api/api/… and 404'd).
        const response = await axios.post('/orders/create/', payload);
        
        // Update the wallet balance and add the new transaction
        if (response.data.status === 'ORDER_PLACED') {
          this.balance -= amount;
          this.transactions.unshift({
            id: response.data.transaction_id,
            amount: amount,
            currency: this.currency,
            type: 'DEBIT',
            orderType: 'FOOD_ORDER',
            status: 'ORDER_PLACED',
            createdAt: new Date(),
            description: `Order #${orderId}`,
          });
        }
        
        return response.data;
      } catch (err) {
        this.error = err.response?.data?.message || 'Failed to make payment.';
        console.error('Error making payment:', err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
  },
});
